import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useGrossCodesAdmin,
  useCreateGrossCode,
  useUpdateGrossCode,
  useToggleGrossCodeActive,
} from "../../../hooks/admin/useGrossCodes";
import GrossCodeForm, {
  GrossCodeFormValues,
} from "../../../components/admin/grosscodes/GrossCodeForm";
import { GrossCode } from "../../../services/admin/grosscodes.service";
import { stripHtml } from "../../../utils/grossTemplate";
import { getFragmentCountDisplayValue } from "../../../utils/fragmentCount";

interface GrossCodeFilters {
  status?: "active" | "inactive" | "all";
}

const ITEMS_PER_PAGE = 20;

export default function GrossCodesAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<GrossCode | null>(null);
  const [togglingItem, setTogglingItem] = useState<GrossCode | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<GrossCodeFilters>({});
  const [pendingFilters, setPendingFilters] = useState<GrossCodeFilters>({});
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: grossCodes = [], isLoading, refetch } = useGrossCodesAdmin();
  const createGrossCode = useCreateGrossCode();
  const updateGrossCode = useUpdateGrossCode();
  const toggleGrossCodeActive = useToggleGrossCodeActive();

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = grossCodes;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(searchLower) ||
          (item.description &&
            stripHtml(item.description).toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((item) => item.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((item) => !item.isActive);
    }

    return result;
  }, [grossCodes, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active
  const hasActiveFilters = filters.status && filters.status !== "all";

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: GrossCode) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: GrossCodeFormValues) => {
    if (editing) {
      await updateGrossCode.mutateAsync({
        id: editing.id,
        ...values,
      });
    } else {
      await createGrossCode.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: GrossCode) => {
    setTogglingItem(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (togglingItem) {
      await toggleGrossCodeActive.mutateAsync({
        id: togglingItem.id,
        isActive: !togglingItem.isActive,
      });
      closeConfirmModal();
      setTogglingItem(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({});
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Accessioning Gross Codes</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage gross code templates for accessioning workflow
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by code or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => refetch()}
                      loading={isLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status === "active" && (
                      <Badge variant="light" color="green">
                        Active Only
                      </Badge>
                    )}
                    {filters.status === "inactive" && (
                      <Badge variant="light" color="red">
                        Inactive Only
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} gross code
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Gross Codes ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No gross codes match the current filters"
                    : "No gross codes found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 150 }}>Code</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th style={{ width: 120 }}>Fragment Count</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        {/* Code */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.code}
                          </Text>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description
                              ? stripHtml(item.description)
                              : "—"}
                          </Text>
                        </Table.Td>

                        {/* Fragment Count */}
                        <Table.Td>
                          <Text size="sm">
                            {getFragmentCountDisplayValue(
                              item.fragmentCount ?? null,
                              item.fragmentCountQualifier ?? null
                            ) || "—"}
                          </Text>
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editing ? "Edit Gross Code Template" : "Add New Gross Code Template"
          }
          size="lg"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <GrossCodeForm
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createGrossCode.isPending || updateGrossCode.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Gross Codes"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: value as "active" | "inactive" | "all" | undefined,
                })
              }
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            togglingItem?.isActive
              ? "Deactivate Gross Code"
              : "Activate Gross Code"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {togglingItem?.isActive ? "deactivate" : "activate"} gross code{" "}
              <strong>{togglingItem?.code}</strong>?
            </Text>
            {togglingItem?.isActive && (
              <Text size="sm" c="dimmed">
                Inactive gross codes will not appear in dropdowns during
                accessioning.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={togglingItem?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleGrossCodeActive.isPending}
              >
                {togglingItem?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
